import React from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  TrashIcon,
  VideoCameraIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { Playlist, AssignmentResult } from "../../schemas/playlistSchema";
import { ItemTypes } from "../../constants";
import { getFullMediaUrl } from "../../utils/mediaUtils";
import { isSameOrder } from "../../utils/playlistOrder";
import { useDragAutoScroll } from "../../hooks/useDragAutoScroll";
import PausedBadge from "../common/PausedBadge";

interface PlaylistCreativeListProps {
  creatives: AssignmentResult[];
  onDelete: (creativeId: string) => void;
  onUpdateCount: (creativeId: string, count: number) => void;
  onReorder?: (assignmentIds: string[]) => void;
  selectedPlaylist: Playlist | null;
  className?: string;
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  // Where a library item currently being dragged would be inserted. Renders the
  // drop indicator line; null hides it.
  insertIndex?: number | null;
}

const DraggableItem: React.FC<{
  item: AssignmentResult;
  index: number;
  onDelete: (creativeId: string) => void;
  onUpdateCount: (creativeId: string, count: number) => void;
  selectedPlaylist: Playlist | null;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}> = ({
  item,
  index,
  onDelete,
  onUpdateCount,
  selectedPlaylist,
  moveItem,
  onDragStart,
  onDragEnd,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const onDragStartRef = React.useRef(onDragStart);
  const onDragEndRef = React.useRef(onDragEnd);
  onDragStartRef.current = onDragStart;
  onDragEndRef.current = onDragEnd;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.PLAYLIST_ITEM,
      item: () => {
        onDragStartRef.current();
        return { id: item.id, index };
      },
      end: () => {
        onDragEndRef.current();
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [item.id, index],
  );

  const [, drop] = useDrop({
    accept: ItemTypes.PLAYLIST_ITEM,
    hover: (draggedItem: { id: string; index: number }, monitor) => {
      if (draggedItem.index !== index) {
        // Only update local state during hover (for visual feedback)
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  drag(drop(ref));

  // Helper to determine if item is a playlist
  const isPlaylist = (item: any) => {
    if (item.type === "main" || item.type === "sub") {
      return true;
    }
    if (selectedPlaylist?.type === "main") {
      return true;
    }
    return false;
  };

  // Helper to determine file type from name or URL
  const getFileType = (item: any): "image" | "video" | "unknown" => {
    const name = item.name || "";
    const url = item.fileUrl || "";
    const fileName = name.toLowerCase() || url.toLowerCase();

    if (
      fileName.includes(".mp4") ||
      fileName.includes(".mov") ||
      fileName.includes(".avi") ||
      fileName.includes(".mkv") ||
      fileName.includes(".webm") ||
      fileName.includes("video")
    ) {
      return "video";
    }

    if (
      fileName.includes(".jpg") ||
      fileName.includes(".jpeg") ||
      fileName.includes(".png") ||
      fileName.includes(".gif") ||
      fileName.includes(".webp") ||
      fileName.includes(".svg") ||
      fileName.includes("image")
    ) {
      return "image";
    }

    return "unknown";
  };

  // Helper to get thumbnail URL for creative (use thumbnailUrl from assignment)
  const getCreativeThumbnailUrl = (item: any) => {
    if (item.thumbnailUrl) {
      return getFullMediaUrl(item.thumbnailUrl);
    }
    return null;
  };

  const itemIsPlaylist = isPlaylist(item);
  let thumbnailUrl: string | null = null;
  let fileType: "image" | "video" | "playlist" | "unknown" = "unknown";

  if (itemIsPlaylist) {
    fileType = "playlist";
  } else {
    const creativeFileType = getFileType(item);
    fileType = creativeFileType;
    thumbnailUrl = getCreativeThumbnailUrl(item);
  }

  return (
    <div
      ref={ref}
      // Marks the card for pointer hit-testing when a library item is dragged
      // over the list (see getInsertIndexFromPointer).
      data-playlist-item
      data-index={index}
      className={`bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 ${
        isDragging ? "opacity-50" : ""
      } ${item.isPaused ? "opacity-60" : ""} cursor-move`}
    >
      {/* Image/Thumbnail Section */}
      <div className="relative w-full aspect-video flex items-center justify-center">
        {fileType === "playlist" ? (
          <div className="flex items-center justify-center">
            <img
              src="/assets/images/playlist-icon.jpg"
              alt="Playlist"
              className="w-20 h-20"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={item.name || "Preview"}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.nextElementSibling?.classList.remove("hidden");
            }}
          />
        ) : null}
        {fileType !== "playlist" && !thumbnailUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <VideoCameraIcon className="h-16 w-16 text-primary-default opacity-50" />
              <PlayIcon className="h-8 w-8 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>
        )}
        {item.isPaused && (
          <div className="absolute top-2 right-2">
            <PausedBadge />
          </div>
        )}
      </div>

      {/* Name Section */}
      <div className="px-3 py-1 bg-primary-default">
        <span className="text-sm text-white font-medium truncate block">
          {item.name || "Unnamed"}
        </span>
      </div>

      {/* Controls Section (Count and Delete) */}
      <div className="px-3 py-1 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <button
            className="h-6 w-6 flex items-center justify-center hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed bg-white border border-gray-300"
            onClick={() =>
              onUpdateCount(item.id, (Number(item.repetitionCount) || 1) - 1)
            }
            disabled={(Number(item.repetitionCount) || 1) <= 1}
            title="Decrease count"
          >
            <span className="text-lg font-semibold text-gray-700">-</span>
          </button>
          <span className="w-8 text-center text-sm font-semibold text-gray-700 py-1">
            {item.repetitionCount || 1}
          </span>
          <button
            className="h-6 w-6 flex items-center justify-center hover:bg-gray-200 rounded bg-white border border-gray-300"
            onClick={() =>
              onUpdateCount(item.id, (Number(item.repetitionCount) || 1) + 1)
            }
            title="Increase count"
          >
            <span className="text-lg font-semibold text-gray-700">+</span>
          </button>
        </div>
        <button
          className="p-1 hover:bg-red-100 rounded bg-white border border-red-300"
          onClick={() => onDelete(item.id)}
          title="Delete item"
        >
          <TrashIcon className="h-4 w-4 text-danger-default" />
        </button>
      </div>
    </div>
  );
};

const PlaylistCreativeList: React.FC<PlaylistCreativeListProps> = ({
  creatives,
  onDelete,
  onUpdateCount,
  onReorder,
  selectedPlaylist,
  className = "space-y-3",
  scrollContainerRef,
  insertIndex = null,
}) => {
  const [items, setItems] = React.useState<AssignmentResult[]>([]);

  React.useEffect(() => {
    // Keep one display list as source of truth for drag/drop ordering.
    // Sorting here ensures backend order is preserved on initial load.
    const sortedCreatives = [...creatives].sort((a, b) => {
      const orderA = a.playOrder ?? 0;
      const orderB = b.playOrder ?? 0;
      return orderA - orderB;
    });
    setItems(sortedCreatives);
  }, [creatives]);

  // Track if we're currently dragging to avoid unnecessary updates
  const isDraggingRef = React.useRef(false);
  const itemsRef = React.useRef<AssignmentResult[]>([]);
  const initialOrderRef = React.useRef<string[]>([]); // Track initial order when drag starts
  const onReorderRef = React.useRef(onReorder);
  React.useEffect(() => {
    onReorderRef.current = onReorder;
  }, [onReorder]);

  // Update ref when items change (but not during drag)
  React.useEffect(() => {
    if (!isDraggingRef.current) {
      itemsRef.current = items;
    }
  }, [items]);

  // Update local state during drag (for visual feedback only)
  const moveItem = React.useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (!isDraggingRef.current) return;

      setItems((prevItems) => {
        const newItems = [...prevItems];
        const [removed] = newItems.splice(dragIndex, 1);
        newItems.splice(hoverIndex, 0, removed);
        // Update ref immediately for accurate tracking
        itemsRef.current = newItems;
        return newItems;
      });
    },
    [],
  );

  // Handle drag end - persist the current UI order, unless it did not change
  const handleDragEnd = React.useCallback(() => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;

    // Get the final order from ref (most up-to-date)
    const finalOrder = itemsRef.current.map((item) => item.id);
    const initialOrder = initialOrderRef.current;
    // Reset initial order
    initialOrderRef.current = [];

    // Dropping an item back where it started is not a reorder — skip the
    // request so it does not report a change that never happened.
    if (isSameOrder(initialOrder, finalOrder)) return;

    if (onReorderRef.current && finalOrder.length > 0) {
      onReorderRef.current(finalOrder);
    }
  }, []);

  // Scroll the list when an item is dragged to its top or bottom edge. Covers
  // both reordering and dragging a new item in from the library.
  useDragAutoScroll(scrollContainerRef);

  // Handle drag start
  const handleDragStart = React.useCallback(() => {
    isDraggingRef.current = true;
    // Store initial order when drag starts
    initialOrderRef.current = itemsRef.current.map((item) => item.id);
  }, []);

  // Line showing where a dragged library item would land.
  const dropIndicator = (
    <div
      data-testid="playlist-drop-indicator"
      className="h-1 rounded bg-primary-default"
    />
  );

  return (
    <div className={className}>
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          {insertIndex === index && dropIndicator}
          <DraggableItem
            item={item}
            index={index}
            onDelete={onDelete}
            onUpdateCount={onUpdateCount}
            selectedPlaylist={selectedPlaylist}
            moveItem={moveItem}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        </React.Fragment>
      ))}
      {insertIndex !== null &&
        items.length > 0 &&
        insertIndex >= items.length &&
        dropIndicator}
      {items.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          {selectedPlaylist?.type === "main"
            ? "Drag and drop sub playlists from the library"
            : "Drag and drop creatives from the library"}
        </div>
      )}
    </div>
  );
};

export default PlaylistCreativeList;
